import { capitalize } from './text.utils';

interface Breadcrumb {
  label?: string;
  slug?: string;
}

const homeAddress = {
  label: 'Home',
  slug: '/',
};

/**
 * Generate breadcrumbs based on the path directories
 * @param directories - array of preceding path directories, e.g. ['insights'] for '/insights/holiday-shipping-guide'
 * @returns array of breadcrumbs including Home, with each breadcrumb containing a slug and the title for that path
 * @example
 * const breadcrumbs = autoGeneratedBreadcrumb(['insights'])
 * // breadcrumbs = [
 * //   { label: 'Home', slug: '/' },
 * //   { label: 'Insights', slug: '/insights' },
 * // ]
 */
const autoGeneratedBreadcrumb = (directories: string[]) => {
  return [
    homeAddress,
    ...directories.map((directory: string, index: number) => {
      const slug = '/' + directories.slice(0, index + 1).join('/');
      const label = capitalize(directory);

      return { label, slug };
    }),
  ];
};

type GetPreviousLevels = (asPath: string) => string[];

/**
 * Get the preceding path directories for the current path (excludes the current page segment)
 * @param asPath - current path, e.g. '/insights/holiday-shipping-guide'
 * @returns array of preceding path directories, e.g. ['insights']
 * @example
 * const previousLevels = getPathDirectories('/insights/holiday-shipping-guide')
 * // previousLevels = ['insights']
 */
export const getPathDirectories: GetPreviousLevels = (asPath) => {
  const levels = asPath.split('/').filter(Boolean);
  return levels.slice(0, -1);
};

type GetBreadcrumbsData = (params: {
  asPath: string;
  title?: string;
}) => Breadcrumb[] | undefined | null;

/**
 * Get breadcrumbs array based on the current path and current page title
 * @param asPath - current path, e.g. '/insights/holiday-shipping-guide'
 * @param title - current page title, e.g. 'Holiday Shipping Guide'
 * @returns array of breadcrumbs, with each breadcrumb containing a slug and the title for that path (except for the current page, which only contains the title)
 * @example
 * const breadcrumbs = getBreadcrumbData({
 *  asPath: '/insights/holiday-shipping-guide',
 *  title: 'Holiday Shipping Guide',
 * })
 * // breadcrumbs = [
 * //   { label: 'Home', slug: '/' },
 * //   { label: 'Insights', slug: '/insights' },
 * //   { label: 'Holiday Shipping Guide' },
 * // ]
 */
export const getBreadcrumbData: GetBreadcrumbsData = ({ asPath, title }) => {
  const pathDirectories = getPathDirectories(asPath);

  return [...autoGeneratedBreadcrumb(pathDirectories), { label: title }];
};
